import {
  Box,
  FormControlLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  Checkbox,
  Radio,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import deepmerge from '@mui/utils/deepmerge';
import React, { useEffect } from 'react';
import { numberWithCommas } from '../../utils/index';
import { FilterDrawerProps } from '.';
import styles from './styles';
import { bedroomsOptions } from '../../utils/Constants/bedrromsOptions';

interface FilterProps {
  value: any;
  onChange: (name: string, value: any) => void;
  searchType: FilterDrawerProps['filterType'];
  allFilters: any[];
}

interface PriceRangeSpecificProps {
  disableScrollLock?: boolean;
  isLabelHidden?: boolean;
  width?: string;
  getEmptyValueLabel?: (name: string) => string;
}

const isTrue = (value: string | boolean) => value === true || value === 'true';

const searchRadiusOptions = [...Array(Math.floor(5 / 0.25) + 1).keys()].map(
  (i) => (i === 0 ? '' : i * 0.25)
);

export function Distance({ value, onChange }: FilterProps) {
  return (
    <>
      <InputLabel sx={styles.inputLabel}>
        <FilterLabel>Search Radius</FilterLabel>
      </InputLabel>
      <Select
        fullWidth
        value={value}
        name="distance"
        displayEmpty
        onChange={(e) => onChange(e.target.name, e.target.value)}
        sx={styles.selectInput}
        IconComponent={KeyboardArrowDown}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300 // Adjust the maxHeight as needed
            }
          }
        }}
        inputProps={{
          'aria-label': 'Select search radius'
        }}
      >
        {searchRadiusOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Typography variant="subtitle2">
              {option === '' ? 'Just this area' : `${option} miles`}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </>
  );
}

const pricePeriodOptions = [
  { label: 'Week', value: 'PerWeek' },
  { label: 'Month', value: 'PerMonth' }
];

export function PricePeriod({ value, onChange }: FilterProps) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <>
      <InputLabel sx={deepmerge(styles.inputLabel, styles.radioGroupLabel)}>
        <Typography variant="h3">Price Per</Typography>
      </InputLabel>
      <RadioGroup
        row={isLargeScreen}
        aria-labelledby="row-radio-price-period"
        name="pricePeriod"
        value={value}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        sx={styles.radioGroup}
      >
        {pricePeriodOptions.map((option) => (
          <FormControlLabel
            sx={styles.groupItem}
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </>
  );
}

const letPriceMonthlyOptions = [
  { value: '', label: '' },
  { value: '200', label: numberWithCommas(867) },
  { value: '250', label: numberWithCommas(1083) },
  { value: '300', label: numberWithCommas(1300) },
  { value: '350', label: numberWithCommas(1517) },
  { value: '400', label: numberWithCommas(1733) },
  { value: '450', label: numberWithCommas(1950) },
  { value: '500', label: numberWithCommas(2167) },
  { value: '600', label: numberWithCommas(2600) },
  { value: '700', label: numberWithCommas(3033) },
  { value: '800', label: numberWithCommas(3467) },
  { value: '900', label: numberWithCommas(3900) },
  { value: '1000', label: numberWithCommas(4333) },
  { value: '1100', label: numberWithCommas(4767) },
  { value: '1200', label: numberWithCommas(5200) },
  { value: '1300', label: numberWithCommas(5633) },
  { value: '1400', label: numberWithCommas(6067) },
  { value: '1500', label: numberWithCommas(6500) },
  { value: '1600', label: numberWithCommas(6933) },
  { value: '1700', label: numberWithCommas(7367) },
  { value: '1750', label: numberWithCommas(7583) },
  { value: '2000', label: numberWithCommas(8667) },
  { value: '2250', label: numberWithCommas(9750) },
  { value: '2500', label: numberWithCommas(10833) },
  { value: '2750', label: numberWithCommas(11917) },
  { value: '3000', label: numberWithCommas(1300) },
  { value: '3500', label: numberWithCommas(15167) },
  { value: '4000', label: numberWithCommas(17333) },
  { value: '4500', label: numberWithCommas(19500) },
  { value: '5000', label: numberWithCommas(21667) },
  { value: '5500', label: numberWithCommas(23833) },
  { value: '6000', label: numberWithCommas(26000) },
  { value: '6500', label: numberWithCommas(28167) },
  { value: '7000', label: numberWithCommas(30333) },
  { value: '8000', label: numberWithCommas(34667) },
  { value: '9000', label: numberWithCommas(39000) },
  { value: '10000', label: numberWithCommas(43333) }
];

const letPriceWeeklyOptions = letPriceMonthlyOptions.map((price) => ({
  value: price.value,
  label: numberWithCommas(Number(price.value))
}));

const salePriceOptions = [
  '',
  '50000',
  '75000',
  '100000',
  '110000',
  '120000',
  '130000',
  '140000',
  '150000',
  '160000',
  '170000',
  '180000',
  '190000',
  '200000',
  '210000',
  '220000',
  '230000',
  '240000',
  '250000',
  '260000',
  '270000',
  '280000',
  '290000',
  '300000',
  '325000',
  '350000',
  '375000',
  '400000',
  '425000',
  '450000',
  '475000',
  '500000',
  '550000',
  '600000',
  '650000',
  '750000',
  '850000',
  '950000',
  '1050000',
  '1150000',
  '1250000',
  '1350000',
  '1450000',
  '1550000',
  '1650000',
  '1750000',
  '1850000',
  '1950000',
  '2050000',
  '2150000',
  '2250000',
  '2350000',
  '2450000',
  '2550000',
  '2650000',
  '2750000',
  '2850000',
  '2950000',
  '3050000'
];

const getPriceArray = (
  searchType: 'sale' | 'letting' | 'sold' | 'short_letting',
  isPricePerMonth: boolean
) => {
  if (searchType === 'letting' || searchType === 'short_letting') {
    return isPricePerMonth ? letPriceMonthlyOptions : letPriceWeeklyOptions;
  }

  return salePriceOptions.map((price) => ({
    value: price,
    label: numberWithCommas(Number(price))
  }));
};

const getPriceLabel = (
  value: string,
  type: string,
  label: string,
  emptyValueLabel = ''
) => {
  if (value === '') {
    if (emptyValueLabel) {
      return emptyValueLabel;
    }

    if (type === 'price_from') {
      return 'No minimum price';
    }

    return 'No maximum price';
  }

  return `£${label}`;
};

function FilterLabel(props) {
  return <Typography variant="subtitle2" {...props} />;
}

const PriceRangeOptions = (
  type: 'price_from' | 'price_to',
  arr: {
    value: string;
    label: string;
  }[],
  emptyValueLabel = ''
) =>
  arr.map((option) => (
    <MenuItem key={`${option.value}_${type}`} value={option.value}>
      <Typography variant="subtitle2">
        {getPriceLabel(option.value, type, option.label, emptyValueLabel)}
      </Typography>
    </MenuItem>
  ));

export function PriceRange({
  value,
  onChange,
  searchType,
  allFilters,
  isLabelHidden,
  width = 'auto',
  getEmptyValueLabel,
  disableScrollLock = false
}: FilterProps & PriceRangeSpecificProps) {
  const isPerMonth =
    allFilters?.find((filter) => filter.name === 'pricePeriod')?.value ===
    'PerMonth';

  const minPriceHandleChange = (name: string, val: number | string) => {
    if (value?.[1] !== '' && Number(val) > Number(value?.[1])) {
      onChange(name, [val, val]);
    } else {
      onChange(name, [val, value?.[1]]);
    }
  };

  const maxPriceHandleChange = (name: string, val: number | string) => {
    if (value?.[0] !== '' && Number(val) < Number(value?.[0])) {
      onChange(name, [val, val]);
    } else {
      onChange(name, [value?.[0], val]);
    }
  };

  return (
    <>
      {isLabelHidden ? null : (
        <InputLabel sx={styles.inputLabel}>
          <FilterLabel>Price</FilterLabel>
        </InputLabel>
      )}
      <Box sx={styles.priceRange} style={{ width }}>
        <Select
          fullWidth
          value={value?.[0] || ''}
          name="price"
          displayEmpty
          onChange={(e) => minPriceHandleChange(e.target.name, e.target.value)}
          sx={styles.selectInput}
          IconComponent={KeyboardArrowDown}
          MenuProps={{
            disableScrollLock
          }}
          inputProps={{
            'aria-label': 'Select minimum price'
          }}
        >
          {PriceRangeOptions(
            'price_from',
            getPriceArray(searchType, isPerMonth),
            getEmptyValueLabel?.('price_from')
          ).map((menuItem) => menuItem)}
        </Select>
        <Select
          fullWidth
          value={value?.[1] || ''}
          name="price"
          displayEmpty
          onChange={(e) => maxPriceHandleChange(e.target.name, e.target.value)}
          sx={styles.selectInput}
          IconComponent={KeyboardArrowDown}
          MenuProps={{
            disableScrollLock
          }}
          inputProps={{
            'aria-label': 'Select maximum price'
          }}
        >
          {PriceRangeOptions(
            'price_to',
            getPriceArray(searchType, isPerMonth),
            getEmptyValueLabel?.('price_to')
          ).map((menuItem) => menuItem)}
        </Select>
      </Box>
    </>
  );
}

export function NumberOfBeds({ value, onChange }: FilterProps) {
  const getOption = (option: string, index: number, lastIndex: number) => {
    let label = option;

    if (option === '') {
      label = 'Any';
    }

    if (index === lastIndex) {
      label = `${option}+`;
    }

    return <FilterLabel>{label}</FilterLabel>;
  };

  return (
    <>
      <InputLabel sx={deepmerge(styles.inputLabel, styles.radioGroupLabel)}>
        <FilterLabel>Minimum no. of bedrooms</FilterLabel>
      </InputLabel>
      <RadioGroup
        row
        aria-labelledby="row-radio-buttons-group-label"
        name="bedrooms_from"
        value={Number(value) === 0 ? '' : Number(value) > 5 ? '5' : value}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        sx={styles.radioGroup}
      >
        {bedroomsOptions.map((option, index, arr) => (
          <FormControlLabel
            sx={styles.groupItem}
            key={option}
            value={option}
            control={<Radio />}
            label={getOption(option, index, arr.length - 1)}
          />
        ))}
      </RadioGroup>
    </>
  );
}

const propertyOptions = [
  { label: 'Any', value: '' },
  { label: 'Flats / apartments', value: 'flats' },
  { label: 'Site ( land or building )', value: 'sites' },
  { label: 'House', value: 'houses' },
  { label: 'Garage / Parking', value: 'parking' }
];

export function PropertyTypes({ value, onChange }: FilterProps) {
  return (
    <>
      <InputLabel sx={deepmerge(styles.inputLabel, styles.radioGroupLabel)}>
        <FilterLabel>Property types</FilterLabel>
      </InputLabel>
      <RadioGroup
        aria-labelledby="row-radio-buttons-group-label"
        name="prop_type"
        value={value}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        sx={styles.radioGroup}
      >
        {propertyOptions.map((option) => (
          <FormControlLabel
            sx={styles.groupItem}
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={
              <Typography variant="subtitle2" component="span">
                {option.label}
              </Typography>
            }
          />
        ))}
      </RadioGroup>
    </>
  );
}

const rentTypeOptions = [
  { label: 'Long let', value: 'letting' },
  { label: 'Short let', value: 'short_letting' }
];

export function RentTypes({ value, onChange }: FilterProps) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <>
      <InputLabel sx={deepmerge(styles.inputLabel, styles.radioGroupLabel)}>
        Rent type
      </InputLabel>
      <RadioGroup
        row={isLargeScreen}
        aria-labelledby="row-radio-buttons-group-label"
        name="rent_type"
        value={value}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        sx={styles.radioGroup}
      >
        {rentTypeOptions.map((option) => (
          <FormControlLabel
            sx={styles.groupItem}
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </>
  );
}

const buyingOptionsItem = [
  { name: 'shared_ownership', label: 'Shared ownership' },
  { name: 'is_help_to_buy', label: 'Help to buy' },
  { name: 'newhomes', label: 'New homes' },
  { name: 'dev_op', label: 'Homes needing work' }
];

export function BuyingOptions({ value, onChange, allFilters }: FilterProps) {
  const hasAuctionOnly = allFilters.find((filter) => filter.name === 'mustHave')
    .value.available_for_auction;

  const handleChange = (name: string, val: boolean) => {
    onChange('buyingOptions', { ...value, [name]: val });
  };

  useEffect(() => {
    if (hasAuctionOnly) {
      const valueObj = value;

      Object.keys(valueObj).forEach((key) => {
        valueObj[key] = false;
      });
      onChange('buyingOptions', valueObj);
    }
  }, [hasAuctionOnly]);

  return (
    <>
      <InputLabel sx={styles.inputLabel}>
        <FilterLabel>Buying Options</FilterLabel>
      </InputLabel>
      <Box sx={styles.checkboxGroup}>
        {buyingOptionsItem.map((option) => (
          <FormControlLabel
            key={option.name}
            control={
              <Checkbox
                onChange={(e) => handleChange(e.target.name, e.target.checked)}
                disabled={isTrue(hasAuctionOnly)}
              />
            }
            sx={styles.groupItem}
            name={option.name}
            label={<FilterLabel>{option.label}</FilterLabel>}
            checked={isTrue(value[option.name])}
          />
        ))}
      </Box>
    </>
  );
}

const rentingOptionsItem = [
  { name: 'managed', label: 'Managed properties' },
  { name: 'prs', label: 'Purpose built apartments' },
  { name: 'landlord_accept_zero_deposit', label: 'Zero deposit' }
];

export function RentingOptions({ value, onChange }: FilterProps) {
  const handleChange = (name: string, val: boolean) => {
    onChange('rentingOptions', { ...value, [name]: val });
  };

  return (
    <>
      <InputLabel sx={styles.inputLabel}>
        <FilterLabel>Renting Options</FilterLabel>
      </InputLabel>
      <Box sx={styles.checkboxGroup}>
        {rentingOptionsItem.map((option) => (
          <FormControlLabel
            key={option.name}
            control={
              <Checkbox
                onChange={(e) => handleChange(e.target.name, e.target.checked)}
              />
            }
            sx={styles.groupItem}
            name={option.name}
            label={<FilterLabel>{option.label}</FilterLabel>}
            checked={isTrue(value[option.name])}
          />
        ))}
      </Box>
    </>
  );
}

const mustHaveOptions = [
  {
    name: 'has_outdoor_space',
    label: 'Any outside space',
    excludeFor: []
  },
  {
    name: 'has_video',
    label: 'Video tour',
    excludeFor: []
  },
  {
    name: 'isRecentLet',
    label: 'Include recently let',
    excludeFor: ['short_letting', '']
  },
  {
    name: 'isRecentSold',
    label: 'Include recently sold',
    excludeFor: ['short_letting', 'letting']
  },
  {
    name: 'available_for_auction',
    label: 'Auction Only',
    excludeFor: ['short_letting', 'letting']
  }
];

export function MustHave({ value, onChange, allFilters }: FilterProps) {
  const rentTypeValue = allFilters.find(
    (item) => item.name === 'rent_type'
  ).value;

  const handleChange = (name: string, val: boolean) => {
    onChange('mustHave', { ...value, [name]: val });
  };

  useEffect(() => {
    if (rentTypeValue === 'short_letting') {
      onChange('mustHave', { ...value, soldOnly: false });
    }
  }, [rentTypeValue]);

  return (
    <>
      <InputLabel sx={styles.inputLabel}>
        <FilterLabel>Must Haves</FilterLabel>
      </InputLabel>
      <Box sx={styles.checkboxGroup}>
        {mustHaveOptions
          .filter((option) => option.excludeFor.indexOf(rentTypeValue) === -1)
          .map((option) => (
            <FormControlLabel
              key={option.name}
              control={
                <Checkbox
                  onChange={(e) =>
                    handleChange(e.target.name, e.target.checked)
                  }
                />
              }
              sx={styles.groupItem}
              name={option.name}
              label={<FilterLabel>{option.label}</FilterLabel>}
              checked={isTrue(value[option.name])}
            />
          ))}
      </Box>
    </>
  );
}
